import * as R from 'ramda';
import { ApiReducer, makeApiAction, makeApiReducer, makeRsaaTypes } from '../../../../core/api/rsaa';
import { addError, addLoading, noLoadingNoError, getLoading } from '../../../../core/api/module';
import { Endpoints } from '../../../../core/api/endpoints';
import { AppState } from '../../../../core/redux/appState';
import { ActionType } from '../../../../core/redux/actionType';
import { DevPlanFieldListItem } from '../models';
import { toMap } from '../../../../language/arrays';


const KEY_ACTION = ActionType.GetDevActionListItemsRequest;
const ACTIONS    = [
  ActionType.GetDevActionListItemsRequest,
  ActionType.GetDevActionListItemsResponse,
  ActionType.GetDevActionListItemsError,
];


type Payload = DevPlanFieldListItem[];


type Meta = {
  projectId: number;
  languageId: number;
};


/**
 * Dispatch this to retrieve the list of items which can be associated with the dev action list field as answers
 *
 * @param projectId - the database Id for the project the dev plan for which this person should be added
 * @param languageId - the database Id for the language in which the email should be sent (usually just the project
 * default language)
 */
export const getDevActionListItemsAction = (
  projectId: number,
  languageId: number,
) => makeApiAction<Payload, Meta>(
  {
    method:   'POST',
    endpoint: Endpoints.GetDevActionListItems,
    headers:  { 'content-type': 'application/json' },
    body:     JSON.stringify(
      {
        projectId:  projectId,
        languageId: languageId,
      },
    ),
    types:    makeRsaaTypes<Payload, Meta>(
      ACTIONS,
      {
        projectId:  projectId,
        languageId: languageId,
      },
    ),
    bailout:  (state: AppState) => !!getLoading(state, KEY_ACTION, `${projectId}-${languageId}`),
  },
);

const reducer: ApiReducer<Payload, Meta> = (state, action, errorMsg, isLoading, payload) => {
  const { projectId, languageId } = action.meta;

  const metaTag = `${projectId}-${languageId}`;

  if (isLoading) {
    return addLoading(state, KEY_ACTION, metaTag);
  }

  if (errorMsg) {
    return addError(state, KEY_ACTION, errorMsg, metaTag);
  }

  const successState = noLoadingNoError(state, KEY_ACTION, metaTag);

  if (!payload) {
    return successState;
  }

  return R.assocPath(
    ['devPlan', 'listItems', `${languageId}`],
    toMap(payload, item => `${item.compilationCategoryListItemId}`),
    successState,
  );
};

export const getDevActionListItemsReducer = makeApiReducer<Payload, Meta>(ACTIONS, KEY_ACTION, reducer);
